const SITE_URL = 'https://zipcodeautofill.com';
const SITE_NAME = 'ZipCode AutoFill';
const DEFAULT_IMAGE = `${SITE_URL}/logo.png`;
const CONTACT_EMAIL = 'info@zipcodeautofill.com';

const defaultDescription =
  'ZipCode AutoFill brings global ZIP and postal code support to HubSpot, automatically filling city, region, country, and timezone fields.';

export const productFaqs = [
  {
    question: 'What is ZipCode AutoFill?',
    answer:
      'ZipCode AutoFill is a HubSpot integration with global ZIP and postal code support. It automatically fills location fields to help teams shorten forms, clean CRM data, and reduce manual address entry.'
  },
  {
    question: 'Is ZipCode AutoFill a HubSpot plugin?',
    answer:
      'Yes. ZipCode AutoFill connects to HubSpot and can update contact and company records when ZIP code data is added or changed.'
  },
  {
    question: 'What HubSpot fields can ZipCode AutoFill populate?',
    answer:
      'ZipCode AutoFill can populate city, state, province or region, county or district, country, and timezone fields. Teams can configure field mappings for their HubSpot portal. Available fields vary by location.'
  },
  {
    question: 'Does ZipCode AutoFill support global postal codes?',
    answer:
      'Yes. Enable the experimental International lookup option in your portal configuration, then add or change a postal code on a record with its country already populated. The lookup uses both values to find the correct location. Coverage varies, and missing or ambiguous fields remain unchanged.'
  },
  {
    question: 'Who is ZipCode AutoFill best for?',
    answer:
      'ZipCode AutoFill is best for HubSpot teams that collect ZIP codes on forms, run geo-targeted campaigns, manage sales territories, or need cleaner contact and company address data.'
  },
  {
    question: 'Can ZipCode AutoFill backfill existing HubSpot records?',
    answer:
      'Yes. HubSpot users with workflow access can backfill existing records that already contain ZIP codes, helping standardize older CRM data.'
  },
  {
    question: 'Does ZipCode AutoFill work for contacts and companies?',
    answer:
      'Yes. ZipCode AutoFill supports HubSpot contact and company records, so teams can standardize location data across both people and account records.'
  }
];

export const pricingFaqs = [
  {
    question: 'How does ZipCode AutoFill pricing work?',
    answer:
      'ZipCode AutoFill uses credits. Successful standard lookups consume 1 credit; successful international lookups consume 2 credits. Failed lookups and attempts that make no changes do not consume credits. Credits purchased in the standard packs do not expire.'
  },
  {
    question: 'Does ZipCode AutoFill offer monthly subscriptions?',
    answer:
      'Yes. Teams can choose one-time credit purchases or monthly subscriptions that add credits each month.'
  },
  {
    question: 'Are credits charged for invalid ZIP codes?',
    answer:
      'No. Credits are only consumed for successful lookups. If a ZIP code cannot be found or is invalid, no credit is deducted.'
  }
];

export const setupSteps = [
  {
    name: 'Connect your HubSpot account',
    text: 'Authorize ZipCode AutoFill from the dashboard so it can connect to your HubSpot portal.'
  },
  {
    name: 'Review field mappings',
    text: 'Confirm mappings for city, state, province or region, county or district, country, and timezone fields. Enable International lookup for global ZIP and postal code support, then save.'
  },
  {
    name: 'Maintain enough credits',
    text: 'Successful standard lookups consume 1 credit, and successful international lookups consume 2 credits. Keep enough credits available for your workflow volume.'
  },
  {
    name: 'Start using ZIP code autofill',
    text: 'Add or change a ZIP or postal code in HubSpot to populate available configured fields. For international lookups, fill in the country first. Changing only the country does not trigger a lookup.'
  }
];

export const seoPages = {
  '/': {
    path: '/',
    title: 'ZipCode AutoFill | HubSpot ZIP Code AutoFill Plugin',
    description:
      'Global ZIP and postal code support for HubSpot. Automatically fill city, province, region, country, and timezone fields with International lookup.',
    keywords:
      'HubSpot ZIP code autofill, HubSpot postal code plugin, HubSpot address automation, CRM data enrichment, city state autofill',
    faqs: productFaqs
  },
  '/why-us': {
    path: '/why-us',
    title: 'Why ZipCode AutoFill for HubSpot CRM Address Automation',
    description:
      'See why HubSpot teams use ZipCode AutoFill to shorten forms, reduce address-entry errors, backfill records, and standardize CRM location data.',
    keywords:
      'best HubSpot ZIP code plugin, HubSpot address app, HubSpot CRM data quality, HubSpot forms address autofill',
    faqs: productFaqs
  },
  '/setup-guide': {
    path: '/setup-guide',
    title: 'HubSpot ZIP Code AutoFill Setup Guide | ZipCode AutoFill',
    description:
      'Connect ZipCode AutoFill to HubSpot, configure field mappings, and enable International lookup for global ZIP and postal code support.',
    keywords:
      'ZipCode AutoFill setup, HubSpot integration guide, HubSpot ZIP code workflow, HubSpot address mapping',
    howTo: true
  },
  '/pricing': {
    path: '/pricing',
    title: 'ZipCode AutoFill Pricing | HubSpot ZIP Code Lookup Credits',
    description:
      'Simple credit-based pricing for HubSpot ZIP code lookups. Choose one-time credit packs or monthly subscriptions for address autofill automation.',
    keywords:
      'ZipCode AutoFill pricing, HubSpot app pricing, HubSpot ZIP code lookup credits, address autofill pricing',
    faqs: pricingFaqs
  },
  '/support': {
    path: '/support',
    title: 'ZipCode AutoFill Support | HubSpot Address Autofill Help',
    description:
      'Get help with ZipCode AutoFill setup, HubSpot portal configuration, credit questions, and ZIP code address lookup support.',
    keywords:
      'ZipCode AutoFill support, HubSpot address autofill help, HubSpot plugin support'
  },
  '/privacy-policy': {
    path: '/privacy-policy',
    title: 'Privacy Policy | ZipCode AutoFill',
    description:
      'Read how ZipCode AutoFill handles account, HubSpot portal, and support data for the HubSpot ZIP code autofill service.',
    keywords: 'ZipCode AutoFill privacy policy, HubSpot app privacy'
  },
  '/terms-of-service': {
    path: '/terms-of-service',
    title: 'Terms of Service | ZipCode AutoFill',
    description:
      'Review the terms for using ZipCode AutoFill, the HubSpot ZIP code autofill and address automation plugin.',
    keywords: 'ZipCode AutoFill terms, HubSpot app terms'
  }
};

const noindexPrefixes = [
  '/admin',
  '/configure-portal',
  '/credits',
  '/dashboard',
  '/forgot-password',
  '/login',
  '/manage-account',
  '/register',
  '/reset-password',
  '/unsubscribe'
];

export function canonicalFor(path) {
  return `${SITE_URL}${path === '/' ? '' : path}`;
}

export function getSeoForPath(pathname) {
  const normalizedPath = pathname === '' ? '/' : pathname;
  const page = seoPages[normalizedPath];

  if (page) {
    return {
      ...page,
      canonical: canonicalFor(page.path),
      robots: 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
      image: DEFAULT_IMAGE
    };
  }

  const isNoindex = noindexPrefixes.some((prefix) => normalizedPath.startsWith(prefix));

  return {
    path: normalizedPath,
    title: isNoindex ? `${SITE_NAME} Account` : SITE_NAME,
    description: defaultDescription,
    keywords: 'ZipCode AutoFill, HubSpot ZIP code autofill, HubSpot CRM address automation',
    canonical: canonicalFor('/'),
    robots: isNoindex ? 'noindex, nofollow' : 'index, follow',
    image: DEFAULT_IMAGE,
    noindex: isNoindex
  };
}

function organizationSchema() {
  return {
    '@type': 'Organization',
    '@id': `${SITE_URL}/#organization`,
    name: SITE_NAME,
    url: SITE_URL,
    logo: DEFAULT_IMAGE,
    email: CONTACT_EMAIL,
    description:
      'ZipCode AutoFill builds HubSpot address automation software that fills CRM location fields from ZIP codes.'
  };
}

function websiteSchema() {
  return {
    '@type': 'WebSite',
    '@id': `${SITE_URL}/#website`,
    url: SITE_URL,
    name: SITE_NAME,
    publisher: {
      '@id': `${SITE_URL}/#organization`
    }
  };
}

function softwareSchema() {
  return {
    '@type': 'SoftwareApplication',
    '@id': `${SITE_URL}/#software`,
    name: SITE_NAME,
    url: SITE_URL,
    applicationCategory: 'BusinessApplication',
    applicationSubCategory: 'HubSpot CRM integration',
    operatingSystem: 'Web',
    description: defaultDescription,
    creator: {
      '@id': `${SITE_URL}/#organization`
    },
    offers: {
      '@type': 'AggregateOffer',
      priceCurrency: 'USD',
      lowPrice: '40',
      highPrice: '500',
      offerCount: '8',
      url: `${SITE_URL}/pricing`
    },
    featureList: [
      'HubSpot contact and company ZIP code lookup',
      'City, state, county, country, and timezone autofill',
      'Configurable HubSpot field mappings',
      'Workflow-friendly backfills for existing records',
      'Credit-based pricing with no expiration for purchased credits'
    ],
    audience: {
      '@type': 'BusinessAudience',
      audienceType:
        'HubSpot users, CRM administrators, marketers, sales teams, operations teams, and data quality teams'
    }
  };
}

function breadcrumbSchema(meta) {
  if (!meta.path || meta.path === '/') {
    return null;
  }

  return {
    '@type': 'BreadcrumbList',
    '@id': `${canonicalFor(meta.path)}#breadcrumb`,
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: SITE_URL
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: meta.title.replace(` | ${SITE_NAME}`, ''),
        item: canonicalFor(meta.path)
      }
    ]
  };
}

function faqSchema(meta) {
  if (!meta.faqs || meta.faqs.length === 0) {
    return null;
  }

  return {
    '@type': 'FAQPage',
    '@id': `${canonicalFor(meta.path)}#faq`,
    mainEntity: meta.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  };
}

function howToSchema(meta) {
  if (!meta.howTo) {
    return null;
  }

  return {
    '@type': 'HowTo',
    '@id': `${SITE_URL}/setup-guide#howto`,
    name: 'How to set up ZipCode AutoFill with HubSpot',
    description:
      'Connect ZipCode AutoFill to HubSpot and configure address fields so ZIP codes can populate city, state, county, country, and timezone values.',
    step: setupSteps.map((step, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: step.name,
      text: step.text
    }))
  };
}

export function structuredDataFor(meta) {
  if (meta.noindex) {
    return null;
  }

  const graph = [
    organizationSchema(),
    websiteSchema(),
    softwareSchema(),
    breadcrumbSchema(meta),
    faqSchema(meta),
    howToSchema(meta)
  ].filter(Boolean);

  return {
    '@context': 'https://schema.org',
    '@graph': graph
  };
}

export { CONTACT_EMAIL, DEFAULT_IMAGE, SITE_NAME, SITE_URL };
